'use client';

import { LuPackage } from 'react-icons/lu';
import { IoIosCheckmark } from 'react-icons/io';
import { GoTag } from 'react-icons/go';
import { useTranslations } from 'next-intl';
import CurrencyIcon from '@/components/ui/icons/currency-icon';
import { formatPrice } from '@/utils/category';

const BundleDeal = ({ tier_prices, selectedBundle, setSelectedBundle }) => {
  const t = useTranslations('product');

  const handleBundleSelect = (bundleId) => {
    bundleId != selectedBundle
      ? setSelectedBundle(bundleId)
      : setSelectedBundle(null);
  };

  // BundleOption component for better code organization
  const BundleOption = ({ bundle }) => {
    const isSelected = selectedBundle === bundle?.qty;
    const qty = parseFloat(bundle?.qty);
    const percentage = Number(bundle?.percentage);
    const discounted_price = Number(bundle?.discounted_price)?.toFixed(2);

    return (
      <div
        className={`relative overflow-hidden rounded-lg transition-all duration-200 ${isSelected ? 'border-primary border-2 shadow-md' : 'border border-gray-200 hover:border-gray-300'}`}
        role='radio'
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBundleSelect(bundle?.qty);
          }
        }}
        onClick={() => handleBundleSelect(bundle?.qty)}
      >
        {!!percentage && (
          <div
            className={`absolute top-0 right-0 px-2 py-0.5 text-xs font-bold text-white ${isSelected ? 'bg-primary' : 'bg-red-500'}`}
          >
            {t('Save_key')} {percentage}%
          </div>
        )}

        <div className='flex cursor-pointer items-center pt-2'>
          <div
            className={`flex items-center justify-center ${qty == 1 ? 'bg-gray-50' : qty == 2 ? 'bg-blue-50' : 'bg-green-50'} p-3`}
          >
            <div className='relative'>
              <LuPackage
                size={24}
                className={`${qty == 1 ? 'text-gray-500' : qty == 2 ? 'text-blue-600' : 'text-green-600'}`}
              />
              {qty > 1 && (
                <div
                  className={`absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full ${qty == 1 ? 'bg-gray-500' : qty == 2 ? 'bg-blue-600' : 'bg-green-600'} text-xs text-white`}
                >
                  {qty}
                </div>
              )}
            </div>
          </div>

          <div className='flex-1 p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <span className='text-sm font-medium'>
                  {qty} {t('Piece')}
                </span>
                <div className='mt-0.5 text-xs text-gray-500'>
                  {qty == 1 ? (
                    t('Standard price')
                  ) : (
                    <span className='flex items-center gap-1'>
                      {discounted_price} <CurrencyIcon className='size-3.5' />/
                      {t('Piece')}
                    </span>
                  )}
                </div>
              </div>
              <span className='flex items-center gap-1 text-sm font-bold text-red-600'>
                {formatPrice(discounted_price * qty)}{' '}
                <CurrencyIcon className='size-3.5' />
              </span>
            </div>
          </div>

          <div className='px-3'>
            <div
              className={`flex h-5 w-5 items-center justify-center rounded-full border ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'}`}
            >
              {isSelected && (
                <IoIosCheckmark size={20} className='text-white' />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='overflow-hidden rounded-lg border border-blue-200 shadow-sm'>
      <div className='flex items-center gap-2 bg-linear-to-r from-blue-600 to-blue-500 p-3'>
        <GoTag size={18} className='text-white' aria-hidden='true' />
        <h3 className='font-bold text-white'>{t('Bundle Deal')}</h3>
      </div>

      <div className='bg-blue-50 p-4'>
        <p className='mb-3 text-sm text-gray-700'>{t('bundle_deal_desc')}</p>

        <div className='py-2' role='radiogroup' aria-label='Bundle options'>
          <div className='space-y-3'>
            {tier_prices?.map((bundle, index) => (
              <BundleOption key={index} bundle={bundle} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleDeal;
